"use server";

import { formatForRecipient } from "@/lib/format";
import { defineAction } from "@/lib/define-action";
import { z } from "zod";
import { recordAccessAudit, getAccess, usersWithPermission } from "./access";
import { prisma } from "./prisma";
import { isPermissionKey, PERMISSIONS } from "./permissions";
import { createGrantInTx } from "./access-grants";
import { notify } from "./notify";
import { can } from "./access-rules";
import { actionKey, type ActionKind } from "./notification-action-kinds";
import { resolveAction } from "./notification-actions";

const RequestAccessSchema = z.object({
  permission: z.string().refine(isPermissionKey, "Unknown permission"),
  reason: z.string().max(500).optional(),
});

type Result = { ok: true; error?: undefined } | { ok: false; error: string };
type Tx = Parameters<typeof createGrantInTx>[0];
const fail = (error: string): Result => ({ ok: false, error });

/** The caller's access, refused when blocked/deleted/not ACTIVE. */
async function activeAccess(userId: number) {
  const access = await getAccess(userId);
  if (!access) throw new Error("Not allowed");
  if (access.isBlocked || access.isDeleted || access.status !== "ACTIVE") throw new Error("You cannot request access at this time.");
  return access;
}

async function createRequest(tx: Tx, actorId: number, action: string, data: Parameters<Tx["accessRequest"]["create"]>[0]["data"]) {
  const request = await tx.accessRequest.create({ data });
  await recordAccessAudit(tx, { actorId, action, targetType: "access_request", targetId: request.id, after: request });
  return request.id;
}

async function notifyAdmins(userId: number, kind: ActionKind, requestId: number, message: (who: string) => string) {
  const [admins, user] = await Promise.all([usersWithPermission("access:manage"), prisma.user.findUnique({ where: { id: userId } })]);
  if (user && admins.length > 0) {
    await notify({ type: "access_request", category: "account", recipients: admins, message: message(user.name || user.email), link: "/admin/access-requests", actionKey: actionKey(kind, requestId) });
  }
}

export const requestAccess = defineAction({
  guard: "active",
  safe: "Unknown error",
  run: async ({ userId }, input: { permission: string; reason?: string }): Promise<Result> => {
    const access = await activeAccess(userId);
    const data = RequestAccessSchema.parse(input);
    const permConfig = PERMISSIONS[data.permission as keyof typeof PERMISSIONS];

    if (can(access, data.permission)) return fail("You already have this access.");
    if ("dangerous" in permConfig && permConfig.dangerous) return fail("This access can't be requested. Ask an administrator.");
    if ((await prisma.accessRequest.count({ where: { userId, status: "PENDING" } })) >= 5) return fail("You have reached the maximum of 5 pending requests.");
    if (await prisma.accessRequest.findFirst({ where: { userId, permission: data.permission, status: "PENDING" } })) {
      return fail("You already have a pending request for this permission.");
    }

    const requestId = await prisma.$transaction((tx) =>
      createRequest(tx as Tx, userId, "access_request.create", { userId, permission: data.permission, reason: data.reason || null }),
    );
    await notifyAdmins(userId, "access_request", requestId, (who) => `${who} requested access to ${permConfig.label}`);
    return { ok: true };
  },
});

export const requestMoreTime = defineAction({
  guard: "active",
  safe: "Unknown error",
  run: async ({ userId }, input: { requestedDays: number; reason?: string }): Promise<Result> => {
    const access = await getAccess(userId);
    if (!access) throw new Error("Not allowed");
    if (access.kind !== "GUEST") throw new Error("Only guests can request more time.");
    await activeAccess(userId);

    if (input.requestedDays > 180 || input.requestedDays < 1) return fail("Requested days must be between 1 and 180.");
    const now = new Date();
    if (!access.grants.some((g) => g.expiresAt && g.expiresAt < now)) return fail("You don't have any expired access to extend.");
    if (await prisma.accessRequest.findFirst({ where: { userId, kind: "MORE_TIME", status: "PENDING" } })) {
      return fail("You already have a pending request for more time.");
    }

    const requestId = await prisma.$transaction((tx) =>
      createRequest(tx as Tx, userId, "access_request.create_more_time", {
        userId,
        kind: "MORE_TIME",
        permission: "*",
        requestedDays: input.requestedDays,
        reason: input.reason || null,
      }),
    );
    await notifyAdmins(userId, "access_more_time", requestId, (who) => `Guest ${who} asks for ${input.requestedDays} more days of access.`);
    return { ok: true };
  },
});

export const cancelAccessRequest = defineAction({
  guard: "active",
  safe: "Unknown error",
  paths: ["/access-denied"],
  run: async ({ userId }, id: number): Promise<Result> => {
    const access = await getAccess(userId);
    if (!access) throw new Error("Not allowed");
    const request = await prisma.accessRequest.findUnique({ where: { id } });
    if (!request || request.userId !== access.userId) return fail("Request not found");
    if (request.status !== "PENDING") return fail("Only pending requests can be cancelled");

    await prisma.$transaction(async (tx) => {
      const updated = await tx.accessRequest.update({ where: { id }, data: { status: "CANCELLED" } });
      await recordAccessAudit(tx as Tx, { actorId: userId, action: "access_request.cancel", targetType: "access_request", targetId: id, before: request, after: updated });
    });
    return { ok: true };
  },
});

/** Loads a request for review; returns the error text instead when it cannot be decided by this reviewer. */
async function pendingForReview(id: number, reviewerId: number, verb: "approved" | "denied") {
  const request = await prisma.accessRequest.findUnique({ where: { id }, include: { user: true } });
  if (!request) return "Request not found";
  if (request.userId === reviewerId) return "Someone else needs to review your own request.";
  if (request.status !== "PENDING") return `Only pending requests can be ${verb}`;
  return request;
}

const requesterOf = (r: { userId: number; user: { email: string; name: string | null } }) => [{ id: r.userId, email: r.user.email, name: r.user.name || "" }];

export const approveAccessRequest = defineAction({
  guard: "access:manage",
  safe: "Unknown error",
  paths: ["/admin/access-requests"],
  run: async ({ access }, id: number, expiryDays: number | null): Promise<Result> => {
    const request = await pendingForReview(id, access.userId, "approved");
    if (typeof request === "string") return fail(request);

    let expiresAt: Date | null = null;
    if (expiryDays !== null) {
      expiresAt = new Date();
      expiresAt.setDate(expiresAt.getDate() + expiryDays);
    }
    const isMoreTime = request.kind === "MORE_TIME";
    if (isMoreTime) {
      if (!expiresAt) return fail("An expiry is required for more time requests.");
      if (expiryDays !== null && expiryDays > 180) return fail("Maximum extension is 180 days.");
    }

    await prisma.$transaction(async (tx) => {
      let grantId: number | undefined;
      if (isMoreTime) {
        const ninetyDaysAgo = new Date();
        ninetyDaysAgo.setDate(ninetyDaysAgo.getDate() - 90);
        const targets = await tx.accessGrant.findMany({
          where: { userId: request.userId, revokedAt: null, expiresAt: { gte: ninetyDaysAgo, lte: new Date() } },
        });
        for (const grant of targets) await tx.accessGrant.update({ where: { id: grant.id }, data: { expiresAt } });
      } else {
        const grantResult = await createGrantInTx(tx as Tx, access, {
          userId: request.userId,
          permission: request.permission,
          expiresAt,
          reason: `Approved access request #${id}`,
        });
        if (!grantResult.ok) throw new Error(grantResult.error);
        grantId = grantResult.grant.id;
      }
      const updated = await tx.accessRequest.update({
        where: { id },
        data: { status: "APPROVED", decidedById: access.userId, decidedAt: new Date(), ...(grantId && { grantId }) },
      });
      await recordAccessAudit(tx as Tx, {
        actorId: access.userId,
        action: isMoreTime ? "access_request.approve_more_time" : "access_request.approve",
        targetType: "access_request",
        targetId: id,
        before: request,
        after: updated,
      });
    });

    await resolveAction(actionKey(isMoreTime ? "access_more_time" : "access_request", id), access.userId);
    const until = expiresAt ? formatForRecipient(expiresAt, request.user.timeZone, { time: true }) : "";
    await notify({
      type: "access_request_approved",
      category: "account",
      recipients: requesterOf(request),
      message: isMoreTime
        ? `Your access extension was approved until ${until}`
        : `Your access to ${PERMISSIONS[request.permission as keyof typeof PERMISSIONS].label} was approved${until && ` until ${until}`}`,
      link: "/",
    });
    return { ok: true };
  },
});

export const denyAccessRequest = defineAction({
  guard: "access:manage",
  safe: "Unknown error",
  paths: ["/admin/access-requests"],
  run: async ({ access }, id: number, note?: string): Promise<Result> => {
    const request = await pendingForReview(id, access.userId, "denied");
    if (typeof request === "string") return fail(request);

    await prisma.$transaction(async (tx) => {
      const updated = await tx.accessRequest.update({
        where: { id },
        data: { status: "DENIED", decidedById: access.userId, decidedAt: new Date(), decisionNote: note || null },
      });
      await recordAccessAudit(tx as Tx, { actorId: access.userId, action: "access_request.deny", targetType: "access_request", targetId: id, before: request, after: updated });
    });
    await resolveAction(actionKey(request.kind === "MORE_TIME" ? "access_more_time" : "access_request", id), access.userId);
    await notify({
      type: "access_request_denied",
      category: "account",
      recipients: requesterOf(request),
      message: `Your access request to ${PERMISSIONS[request.permission as keyof typeof PERMISSIONS].label} was denied`,
      link: "/",
    });
    return { ok: true };
  },
});
